import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMail, FiPhone, FiMapPin, FiInstagram, FiFacebook, FiTwitter, FiYoutube, FiArrowRight, FiExternalLink } from 'react-icons/fi';
import { GiPartyPopper, GiBalloons, GiCakeSlice, GiStarsStack, GiHeartWings } from 'react-icons/gi';
import '../assets/css/styles.css';

export default function Footer() {
  return (
    <footer className="relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23FF6EC7' fill-opacity='0.3' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'%3E%3C/circle%3E%3Ccircle cx='13' cy='13' r='3'%3E%3C/circle%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>
      
      {/* Main Footer */}
      <div className="bg-gradient-to-br from-pink-200 to-pink-300 relative">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-pink-200 via-purple-200 to-yellow-100 opacity-90"></div>
        
        {/* Decorative lights */}
        <div className="absolute left-0 top-[20%] w-[250px] h-[250px] rounded-full bg-purple-400/10 blur-[100px]"></div>
        <div className="absolute right-0 top-[40%] w-[350px] h-[350px] rounded-full bg-yellow-200/10 blur-[120px]"></div>
        
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto pt-20 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            {/* Brand Section */}
            <div className="md:col-span-5 lg:col-span-4">
              <Link to="/" className="flex items-center gap-3 mb-6 group">
                <div className="relative h-14 w-14 flex items-center justify-center rounded-xl overflow-hidden bg-white backdrop-blur-sm border border-white/10 shadow-inner">
                  <img src="/icon.png" alt="Habba Events Logo" className="w-11 h-11 object-contain relative z-10" />
                </div>
              </Link>
              
              <p className="text-gray-600 mb-6 leading-relaxed text-sm max-w-md">
                Transform your special moments with Habba Events. We offer stunning balloon decorations for birthdays, anniversaries, baby showers, and corporate events with customizable options and professional services to create unforgettable experiences.
              </p>
              
              <div className="rounded-xl overflow-hidden bg-white/20 backdrop-blur-sm p-6 mb-8 border border-white/20">
                <h4 className="text-gray-700 text-base font-semibold mb-4 flex items-center">
                  <span className="h-8 w-8 rounded-lg bg-purple-300/30 flex items-center justify-center text-purple-700 mr-3">
                    <FiMapPin size={16} />
                  </span>
                  Our Location
                </h4>
                <p className="text-gray-600 text-sm mb-4">184/A, 2nd main road, 4th cross, chowdeshwari nagar, laggere, Bangalore - 560058</p>
                <Link to="/contact" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors font-medium text-sm group">
                  <span>Get Directions</span>
                  <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            
            <div className="md:col-span-7 lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-bold text-gray-700 mb-5 pb-2 border-b border-gray-300/50 relative">
                  <span className="relative z-10">Navigation</span>
                  <span className="absolute bottom-0 left-0 w-16 h-0.5 bg-purple-500"></span>
                </h4>
                <ul className="space-y-3">
                  <li><FooterLink to="/">Home</FooterLink></li>
                  <li><FooterLink to="/categories">Categories</FooterLink></li>
                  <li><FooterLink to="/gallery">Gallery</FooterLink></li>
                  <li><FooterLink to="/about">About Us</FooterLink></li>
                  <li><FooterLink to="/contact">Contact</FooterLink></li>
                </ul>
              </div>
              
              {/* Services */}
              <div>
                <h4 className="text-lg font-bold text-gray-700 mb-5 pb-2 border-b border-gray-300/50 relative">
                  <span className="relative z-10">Celebrations</span>
                  <span className="absolute bottom-0 left-0 w-16 h-0.5 bg-purple-500"></span>
                </h4>
                <ul className="space-y-3">
                  <li><FooterLink to="/categories"><span className="flex items-center"><GiCakeSlice className="mr-2" /> Birthday Parties</span></FooterLink></li>
                  <li><FooterLink to="/categories"><span className="flex items-center"><GiHeartWings className="mr-2" /> Anniversary</span></FooterLink></li>
                  <li><FooterLink to="/categories"><span className="flex items-center"><GiStarsStack className="mr-2" /> Baby Showers</span></FooterLink></li>
                  <li><FooterLink to="/categories"><span className="flex items-center"><GiBalloons className="mr-2" /> Premium Decorations</span></FooterLink></li>
                  <li><FooterLink to="/categories"><span className="flex items-center"><GiPartyPopper className="mr-2" /> Bachelor Parties</span></FooterLink></li>
                </ul>
              </div>
              
              {/* Contact & Legals */}
              <div>
                <h4 className="text-lg font-bold text-gray-700 mb-5 pb-2 border-b border-gray-300/50 relative">
                  <span className="relative z-10">Contact & Legal</span>
                  <span className="absolute bottom-0 left-0 w-16 h-0.5 bg-purple-500"></span>
                </h4>
                <ul className="space-y-3 mb-6">
                  <li>
                    <a href="tel:+918970144660" className="text-gray-600 hover:text-gray-800 transition-colors duration-200 flex items-center gap-2 text-sm">
                      <span className="h-6 w-6 rounded-full bg-purple-200/50 flex items-center justify-center text-purple-700 text-xs">
                        <FiPhone size={12} />
                      </span>
                      <span>+918970144660</span>
                    </a>
                  </li>
                  <li>
                    <a href="mailto:habbaeventsblr@gmail.com" className="text-gray-600 hover:text-gray-800 transition-colors duration-200 flex items-center gap-2 text-sm">
                      <span className="h-6 w-6 rounded-full bg-purple-200/50 flex items-center justify-center text-purple-700 text-xs">
                        <FiMail size={12} />
                      </span>
                      <span>habbaeventsblr@gmail.com</span>
                    </a>
                  </li>
                </ul>
                
                <ul className="space-y-3">
                  <li><FooterLink to="/privacy-policy">Privacy Policy</FooterLink></li>
                  <li><FooterLink to="/terms-and-conditions">Terms & Conditions</FooterLink></li>
                  <li><FooterLink to="/refund-policy">Refund Policy</FooterLink></li>
                  <li><FooterLink to="/shipping-and-delivery">Shipping & Delivery</FooterLink></li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Social Media and Newsletter Section */}
          <div className="border-t border-gray-300/50 pt-10 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-5 lg:col-span-4">
                <h4 className="text-base font-medium text-gray-700 mb-3">Follow Us</h4>
                <div className="flex space-x-3">
                  <SocialLink href="#" icon={<FiFacebook />} label="Facebook" />
                  <SocialLink href="#" icon={<FiInstagram />} label="Instagram" />
                  <SocialLink href="#" icon={<FiTwitter />} label="Twitter" />
                  <SocialLink href="#" icon={<FiYoutube />} label="YouTube" />
                </div>
              </div>
              
              <div className="md:col-span-7 lg:col-span-8">
                <div className="flex flex-wrap gap-5 justify-start md:justify-end">
                  {/* Any additional content can go here */}
                </div>
              </div>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-300/50 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-center md:text-left text-gray-600">
              &copy; {new Date().getFullYear()} Habba Events. All rights reserved. Premium Balloon Decoration Services.
            </p>
            <div className="mt-4 md:mt-0 text-xs text-gray-600">
              Designed with <span className="text-pink-500">♥</span> for unforgettable celebrations
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }) {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top after the route changes
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  return (
    <Link
      to={to}
      className="text-gray-600 text-sm hover:text-gray-900 transition-colors duration-200 flex items-center group"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-purple-500/50 mr-2 group-hover:bg-purple-700 transition-colors"></span>
      <span className="group-hover:translate-x-0.5 transition-transform">{children}</span>
    </Link>
  );
}

function SocialLink({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="group h-10 w-10 rounded-xl flex items-center justify-center transition-all duration-300 bg-purple-100 hover:bg-purple-500 text-purple-500 hover:text-white border border-purple-200 hover:border-transparent overflow-hidden relative"
    >
      <span className="absolute inset-0 bg-gradient-to-tr from-pink-500/80 via-purple-500 to-pink-500/80 opacity-0 group-hover:opacity-100 transition-opacity"></span>
      <span className="relative z-10">
        {React.cloneElement(icon, { size: 18 })}
      </span>
    </a>
  );
} 